import type { QuestionnaireItem, QuestionnaireSection } from "@repo/data-store";
import { QuestionGuidance } from "@repo/ui/components/question-guidance";
import { Badge } from "@repo/ui/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@repo/ui/components/ui/collapsible";
import { cn } from "@repo/ui/lib/utils";
import { ChevronRight } from "lucide-react";
import { useState } from "react";

export function QuestionnaireItemView({
  item,
  depth = 0,
  forceOpen,
}: {
  item: QuestionnaireItem | QuestionnaireSection;
  depth?: number;
  forceOpen?: boolean;
}) {
  const [localOpen, setLocalOpen] = useState(false);
  const children = "items" in item ? item.items : [];
  const container = !("kind" in item) || item.kind === "RepeatingGroup";
  const label = "kind" in item && item.kind === "Display" ? item.text : item.label;
  const kind = !("kind" in item) ? "Section" : item.kind === "Question" ? item.value.kind : item.kind;
  const badges = [
    ...("kind" in item && item.kind === "Question" && item.required ? ["Required"] : []),
    ...("kind" in item && item.kind === "RepeatingGroup" ? ["Repeats"] : []),
    ...("conditions" in item && item.conditions?.length
      ? [`${item.conditions.length} condition${item.conditions.length > 1 ? "s" : ""}`]
      : []),
  ];
  const body = (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
      <span className="min-w-0 font-medium line-clamp-1">{label || "Untitled"}</span>
      <div className="flex items-center gap-1.5">
        <Badge variant="secondary" className="text-xs px-1.5 py-0 text-muted-foreground">
          {kind}
        </Badge>
        {badges.map((badge) => (
          <Badge key={badge} variant="secondary" className="text-xs px-1.5 py-0 text-muted-foreground">
            {badge}
          </Badge>
        ))}
      </div>
    </div>
  );
  if (!container)
    return (
      <div className={cn("rounded-md border bg-background p-2 text-sm min-h-12", depth > 0 && "ml-4")}>
        {body}
        {"kind" in item && item.kind === "Question" && (
          <QuestionGuidance help={item.help} options={item.value.kind === "Choice" ? item.value.options : undefined} />
        )}
      </div>
    );
  const open = forceOpen ?? localOpen;
  return (
    <Collapsible
      open={open}
      onOpenChange={setLocalOpen}
      className={cn("rounded-md border bg-background text-sm", depth > 0 && "ml-4")}
    >
      <CollapsibleTrigger className="flex w-full items-center gap-2 p-2 min-h-12 hover:bg-muted/50">
        <ChevronRight className={cn("size-3.5 transition-transform", open && "rotate-90")} />
        {body}
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t px-3 pb-3 pt-2 space-y-2">
        {children.map((child) => (
          <QuestionnaireItemView key={child.id} item={child} depth={depth + 1} forceOpen={forceOpen} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}
